import React from 'react';
import { TrendingUp, BookOpen, Target, Award } from 'lucide-react';
import { useTranslation } from '../translations';

const FeedbackCard = ({ feedback }) => {
  const { t } = useTranslation();
  
  if (!feedback) return null;

  const { strengths = [], improvements = [], next_level = null, recommended_courses = [] } = feedback;

  return (
    <div className="space-y-6">
      {/* Pontos Fortes */}
      {strengths.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4 text-green-700 flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            {t('yourStrengths')}
          </h3>
          <div className="space-y-3">
            {strengths.map((strength, index) => (
              <div 
                key={index}
                className="p-4 bg-green-50 rounded-lg border border-green-200 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-green-900 mb-1">
                      {strength.item}
                    </p>
                    <p className="text-green-700 text-sm">
                      {strength.message}
                    </p>
                  </div>
                  <span className="inline-block px-3 py-1 bg-green-200 text-green-900 rounded-full text-sm font-semibold ml-4">
                    {strength.score}/{strength.max_score}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Oportunidades de Melhoria */}
      {improvements.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4 text-orange-700 flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            {t('improvementOpportunities')}
          </h3>
          <div className="space-y-3">
            {improvements.map((improvement, index) => (
              <div 
                key={index}
                className="p-4 bg-orange-50 rounded-lg border border-orange-200 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="font-semibold text-orange-900">
                    {improvement.item}
                  </p>
                  <span className="inline-block px-3 py-1 bg-orange-200 text-orange-900 rounded-full text-sm font-semibold">
                    {improvement.score}/{improvement.max_score}
                  </span>
                </div>
                <p className="text-orange-700 text-sm mb-2">
                  💡 {improvement.recommendation}
                </p>
                <p className="text-xs text-orange-600 font-medium">
                  {t('potentialGain')}: {improvement.potential_gain}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Próximo Nível */}
      {next_level && next_level.target && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-8 h-8" />
            <h3 className="text-2xl font-bold">{t('nextObjective')}</h3>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-4xl">{next_level.emoji || '🎯'}</span>
            <p className="text-3xl font-bold">
              {t('seal')} {next_level.target}
            </p>
          </div>
          <p className="text-blue-100 text-lg">
            {next_level.message}
          </p>
          {next_level.points_needed > 0 && (
            <div className="mt-4 bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-sm">
                {t('youAreOnly')} <span className="font-bold text-2xl">{next_level.points_needed}</span> {t('pointsFromNextLevel')}!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Cursos Recomendados */}
      {recommended_courses.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4 text-blue-700 flex items-center gap-2">
            <Award className="w-6 h-6" />
            {t('recommendedCourses')}
          </h3>
          <ul className="space-y-2">
            {recommended_courses.map((course, index) => (
              <li 
                key={index}
                className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <span className="text-blue-600 font-bold mt-0.5">→</span>
                <span className="text-blue-900 text-sm">{course}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FeedbackCard;